import { useEffect } from "react";
import type { RefObject } from "react";

/**
 * Reads a length of the scroll padding of the document in pixels.
 *
 * @param property - `scroll-padding-top` or `scroll-padding-bottom`.
 * @returns The length, `0` if it is not a pixel length.
 */
function scrollPadding(property: "scroll-padding-top" | "scroll-padding-bottom"): number {
  const value = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(property),
  );
  const padding = Number.isFinite(value) ? value : 0;

  return padding;
}

/**
 * Returns whether an element lies outside the band that the sticky app bar and the fixed bottom
 * navigation leave free, as described by the scroll padding of the document.
 *
 * @param element - The element.
 * @returns `true` if a part of it is covered.
 */
export function isOutsideScrollPadding(element: HTMLElement): boolean {
  const box = element.getBoundingClientRect();
  const top = scrollPadding("scroll-padding-top");
  const bottom = window.innerHeight - scrollPadding("scroll-padding-bottom");
  const outside = box.top < top || box.bottom > bottom;

  return outside;
}

/**
 * Returns whether an element shows keyboard focus. Engines without `:focus-visible` count as no
 * keyboard focus, so nothing moves there.
 *
 * @param element - The focused element.
 * @returns `true` for keyboard focus.
 */
function hasKeyboardFocus(element: HTMLElement): boolean {
  let keyboard: boolean;

  try {
    keyboard = element.matches(":focus-visible");
  } catch {
    keyboard = false;
  }

  return keyboard;
}

/**
 * Scrolls a focused element into the band that the scroll padding leaves free if it shows keyboard
 * focus and a part of it is covered.
 *
 * @param target - The element that received the focus.
 */
function keepInView(target: HTMLElement): void {
  if (
    document.activeElement === target &&
    hasKeyboardFocus(target) &&
    isOutsideScrollPadding(target)
  ) {
    target.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
}

/**
 * Keeps a control that receives the keyboard focus inside a container clear of the sticky app bar
 * and the fixed bottom navigation (WCAG 2.4.11 Focus Not Obscured, UX-103). Browsers skip their
 * own scrolling for a control that is inside the viewport but behind the bottom navigation, so the
 * control is scrolled into the band that the scroll padding leaves free: right away, and again
 * after the next frame in case the browser scrolled on its own. Only keyboard focus
 * (`:focus-visible`) is handled, so taps do not move the page.
 *
 * @param container - Ref of the container, such as `main`.
 */
export function useKeepFocusInView(container: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const element = container.current;

    if (element === null) {
      return undefined;
    }

    let frame: number | null = null;
    const onFocusIn = (event: FocusEvent): void => {
      const target = event.target;

      if (!(target instanceof HTMLElement) || target === element) {
        return;
      }

      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }

      keepInView(target);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        keepInView(target);
      });
    };
    element.addEventListener("focusin", onFocusIn);
    const cleanup = (): void => {
      element.removeEventListener("focusin", onFocusIn);

      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };

    return cleanup;
  }, [container]);
}
